import type { Vec2 } from '../math/vec';
import { v } from '../math/vec';
import { pointInPolygon, closestPointOnSegment, circlePolygonPush } from '../math/geom';
import type { SurfaceMod } from './tuning';
import type { CarState } from './types';

export interface SurfaceRegion {
  poly: Vec2[];
  name: string;
  mod: SurfaceMod;
}

export interface Ramp {
  rect: Vec2[];
  dir: number;
  airTime: number;
  minSpeed: number;
}

export interface PropPlacement {
  sprite: string;
  pos: Vec2;
  rot: number;
  scale: number;
  layer: 'under' | 'over';
}

export interface Track {
  id: string;
  name: string;
  width: number;
  centreline: Vec2[]; // closed loop, first point is the start/finish line
  walls: Vec2[][]; // convex polygons
  surfaces: SurfaceRegion[];
  ramps: Ramp[];
  falloff: Vec2[][];
  startGrid: { pos: Vec2; heading: number }[]; // 6 entries
  props: PropPlacement[];
  bounds: { min: Vec2; max: Vec2 };
  vehicle: 'sportsCar';
}

export interface PreparedTrack extends Track {
  cumDist: number[];
  length: number;
}

const DEFAULT_SURFACE = { name: 'table', mod: { speed: 1, grip: 1 } };

// How many segments on either side of a progressAlong hint to search. Wide
// enough to absorb a car moving fast between ticks, narrow enough to stay
// cheap and avoid snapping to a nearby-but-far-along part of the loop.
const HINT_WINDOW = 8;

// Precomputes cumulative centreline distance and total loop length so
// progressAlong and pointAtProgress do not re-walk the whole loop on every
// call.
export function prepareTrack(t: Track): PreparedTrack {
  const n = t.centreline.length;
  const cumDist: number[] = new Array(n).fill(0);
  for (let i = 1; i < n; i++) {
    const prev = t.centreline[i - 1]!;
    const cur = t.centreline[i]!;
    cumDist[i] = cumDist[i - 1]! + v.dist(prev, cur);
  }
  const closing = v.dist(t.centreline[n - 1]!, t.centreline[0]!);
  const length = (cumDist[n - 1] ?? 0) + closing;
  return { ...t, cumDist, length };
}

function segmentTangent(a: Vec2, b: Vec2): { dir: Vec2; angle: number; len: number } {
  const len = v.dist(a, b);
  const dir = len === 0 ? { x: 1, y: 0 } : v.scale(v.sub(b, a), 1 / len);
  return { dir, angle: Math.atan2(dir.y, dir.x), len };
}

// Finds where p sits relative to the centreline: progress is the arc-length
// distance along the loop from the start/finish line, lateral is the signed
// perpendicular offset (positive on the right of the direction of travel),
// and tangent is the direction of travel in radians at that point.
//
// Without a hint every segment is checked. With a hint, only segments
// within HINT_WINDOW of the hinted index (wrapping around the loop) are
// checked, which stays cheap and keeps a fast-moving car from being
// attributed to a nearby-but-far-along part of the loop.
export function progressAlong(
  t: PreparedTrack,
  p: Vec2,
  hint?: number,
): { progress: number; lateral: number; tangent: number } {
  const n = t.centreline.length;

  const indices: number[] = [];
  if (hint === undefined) {
    for (let i = 0; i < n; i++) indices.push(i);
  } else {
    const seen = new Set<number>();
    for (let d = -HINT_WINDOW; d <= HINT_WINDOW; d++) {
      const i = ((hint + d) % n + n) % n;
      if (!seen.has(i)) {
        seen.add(i);
        indices.push(i);
      }
    }
  }

  // Tolerance for treating two candidate segments as tied on distance. Near
  // the seam vertex (centreline[0]), the closing segment (index n-1, t=1)
  // and segment 0 (t=0) both sit at distance 0 from a point at the seam, so
  // ties must be broken deterministically (by progress, not iteration
  // order) or the result depends on which segment the hint window visits
  // first.
  const EPS = 1e-9;

  let bestDist = Infinity;
  let bestSeg = indices[0] ?? 0;
  let bestPoint: Vec2 = t.centreline[bestSeg]!;
  let bestT = 0;
  let bestProgress = 0;

  for (const i of indices) {
    const a = t.centreline[i]!;
    const b = t.centreline[(i + 1) % n]!;
    const { point, t: segT } = closestPointOnSegment(p, a, b);
    const d = v.dist(p, point);
    const segLen = v.dist(a, b);
    const candidateProgress = (t.cumDist[i] ?? 0) + segT * segLen;

    if (d < bestDist - EPS) {
      bestDist = d;
      bestSeg = i;
      bestPoint = point;
      bestT = segT;
      bestProgress = candidateProgress;
    } else if (d <= bestDist + EPS && candidateProgress < bestProgress) {
      // Tied within tolerance: prefer the smaller pre-wrap progress so the
      // seam always resolves to progress 0 (segment 0, t=0) rather than
      // progress length (the closing segment, t=1), regardless of which
      // candidate the hint window happened to visit first.
      bestDist = Math.min(bestDist, d);
      bestSeg = i;
      bestPoint = point;
      bestT = segT;
      bestProgress = candidateProgress;
    }
  }

  const a = t.centreline[bestSeg]!;
  const b = t.centreline[(bestSeg + 1) % n]!;
  const { dir, angle } = segmentTangent(a, b);
  const right = { x: -dir.y, y: dir.x };
  const lateral = v.dot(v.sub(p, bestPoint), right);
  const length = t.length;
  const progress = length === 0 ? 0 : ((bestProgress % length) + length) % length;

  return { progress, lateral, tangent: angle };
}

// Inverse of progressAlong: the centreline position and travel direction at
// arc-length s along the loop. s wraps modulo the track length.
export function pointAtProgress(t: PreparedTrack, s: number): { pos: Vec2; tangent: number } {
  const n = t.centreline.length;
  const length = t.length;
  let sMod = length === 0 ? 0 : s % length;
  if (sMod < 0) sMod += length;

  let segIndex = n - 1;
  for (let i = 0; i < n; i++) {
    const start = t.cumDist[i] ?? 0;
    const end = i + 1 < n ? t.cumDist[i + 1]! : length;
    if (sMod >= start && sMod < end) {
      segIndex = i;
      break;
    }
  }

  const a = t.centreline[segIndex]!;
  const b = t.centreline[(segIndex + 1) % n]!;
  const { dir, angle, len } = segmentTangent(a, b);
  const segStart = t.cumDist[segIndex] ?? 0;
  const localT = len === 0 ? 0 : (sMod - segStart) / len;
  const pos = v.lerp(a, b, localT);

  return { pos, tangent: angle };
}

// The surface region a point falls in, or the default table surface if it
// is in none. Regions are checked in order and the first match wins.
export function surfaceAt(t: PreparedTrack, p: Vec2): { name: string; mod: SurfaceMod } {
  for (const region of t.surfaces) {
    if (pointInPolygon(p, region.poly)) {
      return { name: region.name, mod: region.mod };
    }
  }
  return DEFAULT_SURFACE;
}

// Summed minimum-translation push needed to move a circle (centre pos,
// radius r) out of every overlapping wall, or null if it overlaps none.
export function collideWalls(t: PreparedTrack, pos: Vec2, r: number): Vec2 | null {
  let sum: Vec2 = { x: 0, y: 0 };
  let hit = false;
  for (const wall of t.walls) {
    const push = circlePolygonPush(pos, r, wall);
    if (push) {
      sum = v.add(sum, push);
      hit = true;
    }
  }
  return hit ? sum : null;
}

// Resolves a wall collision on car: moves car.pos out by push, cancels the
// component of car.vel pointing into the wall, and applies a 0.85 speed
// loss to whatever velocity remains.
export function resolveWallHit(car: CarState, push: Vec2): void {
  car.pos = v.add(car.pos, push);

  const n = v.norm(push);
  const into = v.dot(car.vel, n);
  let vel = car.vel;
  if (into < 0) {
    vel = v.sub(vel, v.scale(n, into));
  }
  car.vel = v.scale(vel, 0.85);
}

// True if p is inside any falloff (off-track death) region.
export function inFalloff(t: PreparedTrack, p: Vec2): boolean {
  return t.falloff.some((poly) => pointInPolygon(p, poly));
}

// The ramp whose rect contains p, or null if there is none.
export function rampAt(t: PreparedTrack, p: Vec2): Ramp | null {
  for (const ramp of t.ramps) {
    if (pointInPolygon(p, ramp.rect)) return ramp;
  }
  return null;
}
